import {
    memo,
    MouseEvent as ReactMouseEvent,
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState
} from "react";
import { AnimatePresence } from "framer-motion";
import { useCoreApp } from "@rapid/contexts/CoreAppContext";
import { useComputedValues } from "@rapid/contexts/ComputedValuesContext";
import { useFlow } from "@rapid/contexts/FlowContext";
import { useModel } from "@rapid/contexts/ModelContext";
import FilePreview from "@rapid/components/file/Preview";
import { FileCarousel } from "@rapid/components/file/Carousel/FileCarousel";
import { BoundingBoxStrategyImpl } from "@rapid/utils/annotationStrategies/BoundingBox";
import { FilePreviewProvider } from "@rapid/contexts/FilePreviewProvider";
import { useTestWithMoreFiles } from "@rapid/useTestWithMoreFiles";
import {
    useCurrentMedia,
    useSnapshotUserAnnotations,
    useClearUserAnnotationsSnapshot,
    useUserAnnotationsSnapshot,
    useMediaStore,
    useFlattenedVideoSourceId,
    useSetFlattenedVideoSourceId,
    useStore,
    useInferenceQueueItem,
    InferenceStatus
} from "@rapid/store";
import { CurrentMedia } from "@rapid/store/slices/mediaSlice";
import { Annotation, AnnotationSource } from "@rapid/types/annotation";
import { MediaItem, MediaItemStatus } from "@rapid/types/media";
import { Prediction } from "@rapid/utils/api/inference";
import { DEFAULT_CONFIDENCE_THRESHOLD, predictionToAnnotation } from "@rapid/utils/predictions";
import { useMedia } from "@rapid/contexts/MediaContext";
import { useReviewGridView } from "@rapid/contexts/ReviewGridViewContext";
import { ReviewTabs, ReviewTab } from "./ReviewTabs";
import { useReviewTab } from "./ReviewContext";
import { ReviewGridView } from "./ReviewGridView";
import { v4 as uuidv4 } from "uuid";
import Button from "@scripts/react/components/Button";

const MIN_FILES_FOR_GRID_VIEW = 3;

function areEditablePredictionListsEqual(a: Prediction[], b: Prediction[]) {
    if (a.length !== b.length) return false;

    return a.every((prediction, index) => {
        const other = b[index];
        if (!other) return false;

        return (
            prediction.detection_id === other.detection_id &&
            prediction.class === other.class &&
            prediction.class_id === other.class_id &&
            prediction.x === other.x &&
            prediction.y === other.y &&
            prediction.width === other.width &&
            prediction.height === other.height &&
            prediction.confidence === other.confidence &&
            prediction.frameId === other.frameId &&
            prediction.frameSegment === other.frameSegment &&
            prediction.fileId === other.fileId &&
            prediction.thumbnailDataUrl === other.thumbnailDataUrl &&
            prediction.type === other.type &&
            prediction.masks === other.masks
        );
    });
}

function ReviewFilePreview({
    showLabels,
    fileId,
    annotationSource,
    forceViewOnly,
    predictionConfidenceThreshold,
    onAnnotationClick,
    onCanvasClickWithoutAnnotation
}: {
    showLabels: boolean;
    fileId: string | undefined;
    annotationSource?: AnnotationSource;
    forceViewOnly: boolean;
    predictionConfidenceThreshold?: number;
    onAnnotationClick?: (annotation: unknown, event: ReactMouseEvent<HTMLCanvasElement>) => void;
    onCanvasClickWithoutAnnotation?: () => void;
}) {
    return (
        <FilePreview
            key={`file-${fileId}`}
            forceHideLabels={!showLabels}
            strategy={BoundingBoxStrategyImpl}
            includeWatermark={true}
            watermarkQrUrl="https://rapid.roboflow.com"
            annotationSourceOverride={annotationSource}
            forceViewOnly={forceViewOnly}
            predictionConfidenceThresholdOverride={predictionConfidenceThreshold}
            onAnnotationClick={onAnnotationClick}
            onCanvasClickWithoutAnnotation={onCanvasClickWithoutAnnotation}
        />
    );
}

export const ReviewVisualAreaContent = memo(() => {
    const { showLabels } = useCoreApp();
    const { currentFrameAnnotations, currentFramePredictions, currentFrameUserAnnotations } =
        useComputedValues();
    const { setCurrentFrameAnnotations, setCurrentFramePredictions } = useFlow();
    const { promptLabels } = useModel();
    const currentMedia = useCurrentMedia();
    const media = useMediaStore();
    const setCurrentMedia = useStore((state) => state.setCurrentMedia);
    const { testWithMoreFiles, uploadLimitError, clearUploadLimitError } = useTestWithMoreFiles();
    const {
        activeTab,
        setActiveTab,
        savedVideoMedia,
        setSavedVideoMedia,
        isEditingModel,
        startEditingModel,
        updateModelEdits
    } = useReviewTab();
    const { orderedMediaItems } = useMedia();
    const { isGridView, setIsGridView } = useReviewGridView();
    const inferenceQueueItem = useInferenceQueueItem(currentMedia?.file.id);
    const didInitialGridSync = useRef(false);
    const reviewCanvasContainerRef = useRef<HTMLDivElement | null>(null);
    const [isHovered, setIsHovered] = useState(false);
    const [annotationEditPrompt, setAnnotationEditPrompt] = useState<{
        left: number;
        top: number;
    } | null>(null);
    const preparedEditTargetsRef = useRef<Set<string>>(new Set());

    const userAnnotationsSnapshot = useUserAnnotationsSnapshot();
    const flattenedVideoSourceId = useFlattenedVideoSourceId();
    const setFlattenedVideoSourceId = useSetFlattenedVideoSourceId();

    // Snapshot user annotations before inference overwrites them.
    // useLayoutEffect fires synchronously before useEffects (which trigger inference),
    // guaranteeing the snapshot captures the user's original annotations.
    const snapshotUserAnnotations = useSnapshotUserAnnotations();
    const clearUserAnnotationsSnapshot = useClearUserAnnotationsSnapshot();
    useLayoutEffect(() => {
        snapshotUserAnnotations();
        return () => {
            clearUserAnnotationsSnapshot();
            setFlattenedVideoSourceId(null);
        };
    }, [snapshotUserAnnotations, clearUserAnnotationsSnapshot, setFlattenedVideoSourceId]);

    useEffect(() => {
        if (didInitialGridSync.current) return;
        didInitialGridSync.current = true;
        setIsGridView(orderedMediaItems.length >= MIN_FILES_FOR_GRID_VIEW);
    }, [orderedMediaItems.length, setIsGridView]);

    const onAddMediaItems = useCallback(
        async (files: File[], onFinally?: () => void) => {
            await testWithMoreFiles(files, onFinally);
        },
        [testWithMoreFiles]
    );

    // Compute annotated frames for videos. For each video in orderedMediaItems,
    // find extracted frames that have user annotations in the snapshot.
    const annotatedFramesByVideoId = useMemo(() => {
        const result: Record<string, typeof orderedMediaItems> = {};

        for (const item of orderedMediaItems) {
            if (!item.isVideo) continue;

            const fileSnapshot = userAnnotationsSnapshot[item.id];
            if (!fileSnapshot) continue;

            const frames: typeof orderedMediaItems = [];
            for (const [frameId, annotations] of Object.entries(fileSnapshot)) {
                if (!annotations || annotations.length === 0) continue;
                const frameItem = media[frameId];
                if (frameItem && frameItem.status === MediaItemStatus.COMPLETED) {
                    frames.push(frameItem);
                }
            }

            if (frames.length > 0) {
                // Sort by segment so frames appear in timeline order
                frames.sort((a, b) => (a.metadata?.segment ?? 0) - (b.metadata?.segment ?? 0));
                result[item.id] = frames;
            }
        }

        return result;
    }, [orderedMediaItems, userAnnotationsSnapshot, media]);

    const isAnnotationsView = activeTab === ReviewTab.MyAnnotations;
    const hasVideoInMedia = orderedMediaItems.some((item) => item.isVideo);
    const isVideoFlatteningActive = isAnnotationsView && hasVideoInMedia;

    // Build the override list: replace each video with its annotated frames
    const orderedMediaItemsOverride = useMemo(() => {
        if (!isVideoFlatteningActive) return undefined;

        const result: typeof orderedMediaItems = [];
        for (const item of orderedMediaItems) {
            if (item.isVideo && annotatedFramesByVideoId[item.id]) {
                result.push(...annotatedFramesByVideoId[item.id]);
            } else if (!item.isVideo) {
                result.push(item);
            }
            // If a video has no annotated frames, it's simply omitted
        }

        return result;
    }, [isVideoFlatteningActive, orderedMediaItems, annotatedFramesByVideoId]);

    // Track whether we've already handled the tab switch to avoid re-running
    const prevIsVideoFlatteningRef = useRef(false);

    useEffect(() => {
        const wasFlattening = prevIsVideoFlatteningRef.current;
        prevIsVideoFlatteningRef.current = isVideoFlatteningActive;

        if (isVideoFlatteningActive && !wasFlattening) {
            // Switching TO annotations view with a video — save and flatten
            if (currentMedia?.file.isVideo) {
                setSavedVideoMedia(currentMedia);
                setFlattenedVideoSourceId(currentMedia.file.id);

                // Navigate to the first annotated frame (or the current frame if it has annotations)
                const videoFrames = annotatedFramesByVideoId[currentMedia.file.id];
                if (videoFrames && videoFrames.length > 0) {
                    const currentFrameInList = currentMedia.frame
                        ? videoFrames.find((f) => f.id === currentMedia.frame!.id)
                        : undefined;
                    const targetFrame = currentFrameInList ?? videoFrames[0];
                    setCurrentMedia({
                        file: targetFrame,
                        frame: targetFrame,
                        frameSegment: targetFrame.metadata?.segment ?? 0
                    });
                }
            }
        } else if (!isVideoFlatteningActive && wasFlattening) {
            // Switching BACK to predictions view — restore the video
            setFlattenedVideoSourceId(null);

            if (savedVideoMedia) {
                // Try to restore at the frame the user was last viewing
                const currentFrameId = currentMedia?.file.id;
                const videoItem = media[savedVideoMedia.file.id];

                if (videoItem) {
                    // Find the segment for the frame we were viewing
                    let targetSegment = savedVideoMedia.frameSegment ?? 0;
                    let targetFrame = savedVideoMedia.frame;

                    if (currentFrameId && videoItem.segmentToFrameMediaItemIdMap) {
                        for (const [seg, frameId] of Object.entries(
                            videoItem.segmentToFrameMediaItemIdMap
                        )) {
                            if (frameId === currentFrameId) {
                                targetSegment = Number(seg);
                                targetFrame = media[frameId];
                                break;
                            }
                        }
                    }

                    setCurrentMedia({
                        file: videoItem,
                        frame: targetFrame,
                        frameSegment: targetSegment
                    });
                }

                setSavedVideoMedia(null);
            }
        }
    }, [
        isVideoFlatteningActive,
        currentMedia,
        savedVideoMedia,
        annotatedFramesByVideoId,
        media,
        setCurrentMedia,
        setSavedVideoMedia,
        setFlattenedVideoSourceId
    ]);

    useEffect(() => {
        if (!isVideoFlatteningActive || !flattenedVideoSourceId || !currentMedia?.file) return;
        if (currentMedia.file.isVideo) return;

        const frameSegment = currentMedia.file.metadata?.segment ?? 0;
        if (
            currentMedia.frame?.id === currentMedia.file.id &&
            currentMedia.frameSegment === frameSegment
        ) {
            return;
        }

        setCurrentMedia({ file: currentMedia.file, frame: currentMedia.file, frameSegment });
    }, [currentMedia, flattenedVideoSourceId, isVideoFlatteningActive, setCurrentMedia]);

    const currentFrameKey = `${currentMedia?.file.id || "no-file"}:${currentMedia?.frame?.id || currentMedia?.file.id || "no-frame"}:${currentMedia?.frameSegment ?? 0}`;

    const reviewConfidenceThreshold =
        currentMedia?.file.confidenceThreshold ?? DEFAULT_CONFIDENCE_THRESHOLD;
    const isFlattenedVideoFrameSegmentNormalizing =
        isVideoFlatteningActive &&
        Boolean(flattenedVideoSourceId) &&
        Boolean(currentMedia?.file) &&
        !currentMedia?.file.isVideo &&
        (currentMedia?.frame?.id !== currentMedia?.file.id ||
            currentMedia?.frameSegment !== (currentMedia?.file.metadata?.segment ?? 0));

    const classIdByLabel = useMemo(() => {
        return new Map(Array.from(promptLabels.keys()).map((label, index) => [label, index]));
    }, [promptLabels]);

    const visiblePredictionsForCurrentFrame = useMemo(() => {
        return currentFramePredictions.filter(
            (prediction) =>
                promptLabels.has(prediction.class) &&
                prediction.confidence >= reviewConfidenceThreshold
        );
    }, [currentFramePredictions, promptLabels, reviewConfidenceThreshold]);

    const isInferenceRunning =
        inferenceQueueItem?.status === InferenceStatus.QUEUED ||
        inferenceQueueItem?.status === InferenceStatus.INFERRING;
    const canStartEditingCurrentFrame =
        !isGridView && !isInferenceRunning && !isFlattenedVideoFrameSegmentNormalizing;
    const isReviewFrameEditing =
        (activeTab === ReviewTab.DraftModel || activeTab === ReviewTab.MyAnnotations) &&
        !isGridView &&
        isEditingModel;
    const isEditingPredictions = activeTab === ReviewTab.DraftModel && isReviewFrameEditing;
    const editLabel =
        activeTab === ReviewTab.MyAnnotations ? "Edit Annotations" : "Edit Predictions";
    const editPromptLabel = editLabel;
    const annotationSource =
        activeTab === ReviewTab.MyAnnotations
            ? AnnotationSource.Annotations
            : isReviewFrameEditing
              ? AnnotationSource.Annotations
              : AnnotationSource.Predictions;

    const predictionToEditableAnnotation = useCallback(
        (prediction: Prediction): Annotation | null => {
            const frameId = currentMedia?.frame?.id ?? currentMedia?.file.id;
            if (!currentMedia?.file.id || !frameId) return null;

            const annotation = predictionToAnnotation(prediction, frameId);
            const { confidence: _confidence, ...groundTruthAnnotation } = annotation;

            return {
                ...groundTruthAnnotation,
                id: uuidv4(),
                fileId: currentMedia.file.id,
                frameId,
                frameSegment: currentMedia.frameSegment ?? 0
            };
        },
        [currentMedia?.file.id, currentMedia?.frame?.id, currentMedia?.frameSegment]
    );

    const annotationToPrediction = useCallback(
        (annotation: Annotation): Prediction => ({
            x: annotation.x,
            y: annotation.y,
            width: annotation.width,
            height: annotation.height,
            confidence: annotation.confidence ?? 1,
            class: annotation.label,
            class_id: classIdByLabel.get(annotation.label) ?? 0,
            detection_id: annotation.id,
            fileId: annotation.fileId,
            frameId: annotation.frameId,
            frameSegment: annotation.frameSegment ?? currentMedia?.frameSegment ?? 0,
            draft: false,
            ...(annotation.thumbnailDataUrl
                ? { thumbnailDataUrl: annotation.thumbnailDataUrl }
                : {}),
            ...(annotation.masks ? { masks: annotation.masks } : {}),
            ...(annotation.type ? { type: annotation.type } : {})
        }),
        [classIdByLabel, currentMedia?.frameSegment]
    );

    const prepareCurrentFrameForEditing = useCallback(() => {
        if (!canStartEditingCurrentFrame) return;

        const editableAnnotations =
            activeTab === ReviewTab.MyAnnotations
                ? currentFrameUserAnnotations.map((annotation) => ({ ...annotation }))
                : visiblePredictionsForCurrentFrame
                      .map(predictionToEditableAnnotation)
                      .filter((annotation): annotation is Annotation => Boolean(annotation));

        setCurrentFrameAnnotations(editableAnnotations);
        if (activeTab === ReviewTab.DraftModel) {
            const editedPredictions = editableAnnotations.map(annotationToPrediction);
            if (!areEditablePredictionListsEqual(currentFramePredictions, editedPredictions)) {
                setCurrentFramePredictions(editedPredictions);
            }
        }
    }, [
        activeTab,
        annotationToPrediction,
        canStartEditingCurrentFrame,
        currentFramePredictions,
        currentFrameUserAnnotations,
        predictionToEditableAnnotation,
        setCurrentFrameAnnotations,
        setCurrentFramePredictions,
        visiblePredictionsForCurrentFrame
    ]);

    const toggleFrameEditing = useCallback(() => {
        if (isEditingModel) {
            updateModelEdits();
            return;
        }

        if (!canStartEditingCurrentFrame) return;

        startEditingModel();
    }, [canStartEditingCurrentFrame, isEditingModel, startEditingModel, updateModelEdits]);

    useEffect(() => {
        if (!isEditingPredictions) return;

        const editedPredictions = currentFrameAnnotations
            .filter((annotation) => !annotation.draft)
            .map(annotationToPrediction);

        if (!areEditablePredictionListsEqual(currentFramePredictions, editedPredictions)) {
            setCurrentFramePredictions(editedPredictions);
        }
    }, [
        annotationToPrediction,
        currentFrameAnnotations,
        currentFramePredictions,
        isEditingPredictions,
        setCurrentFramePredictions
    ]);

    const editTargetKey = `${activeTab}:${currentFrameKey}`;

    useEffect(() => {
        if (!isEditingModel) {
            preparedEditTargetsRef.current.clear();
            return;
        }

        if (!canStartEditingCurrentFrame || preparedEditTargetsRef.current.has(editTargetKey)) {
            return;
        }

        prepareCurrentFrameForEditing();
        preparedEditTargetsRef.current.add(editTargetKey);
    }, [canStartEditingCurrentFrame, editTargetKey, isEditingModel, prepareCurrentFrameForEditing]);

    useEffect(() => {
        setAnnotationEditPrompt(null);
    }, [currentFrameKey, activeTab, isGridView, isReviewFrameEditing]);

    const gridAnnotationSource =
        activeTab === ReviewTab.MyAnnotations
            ? AnnotationSource.Annotations
            : AnnotationSource.Predictions;

    const showEditPromptAtEvent = useCallback(
        (event?: ReactMouseEvent<HTMLCanvasElement> | ReactMouseEvent<HTMLDivElement>) => {
            if (isReviewFrameEditing || !canStartEditingCurrentFrame || isGridView) return;
            if (!event) return;

            const containerRect = reviewCanvasContainerRef.current?.getBoundingClientRect();
            if (!containerRect) return;

            setAnnotationEditPrompt({
                left: event.clientX - containerRect.left + 12,
                top: event.clientY - containerRect.top - 36
            });
        },
        [canStartEditingCurrentFrame, isGridView, isReviewFrameEditing]
    );

    const handleReviewAnnotationClick = useCallback(
        (_annotation: unknown, event: ReactMouseEvent<HTMLCanvasElement>) => {
            showEditPromptAtEvent(event);
        },
        [showEditPromptAtEvent]
    );

    const handleEditPromptClick = useCallback(() => {
        setAnnotationEditPrompt(null);
        if (!canStartEditingCurrentFrame) return;
        startEditingModel();
    }, [canStartEditingCurrentFrame, startEditingModel]);

    const handleSelectMediaFromGrid = useCallback(
        (mediaItem: MediaItem) => {
            const storeItem = media[mediaItem.id];
            if (!storeItem) return;

            const updatedCurrentMedia: CurrentMedia = { file: storeItem };
            if (storeItem.isVideo) {
                const frame0 = storeItem.frame0Id ? media[storeItem.frame0Id] : undefined;
                if (frame0) {
                    updatedCurrentMedia.frame = frame0;
                    updatedCurrentMedia.frameSegment = 0;
                }
            } else {
                updatedCurrentMedia.frame = storeItem;
                updatedCurrentMedia.frameSegment =
                    flattenedVideoSourceId && !storeItem.isVideo
                        ? (storeItem.metadata?.segment ?? 0)
                        : 0;
            }

            setCurrentMedia(updatedCurrentMedia);
            setIsGridView(false);
        },
        [flattenedVideoSourceId, media, setCurrentMedia, setIsGridView]
    );

    const toggleGridView = useCallback(() => setIsGridView((prev) => !prev), [setIsGridView]);

    return (
        <AnimatePresence mode="wait">
            <div
                className="visualArea"
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
            >
                <div className="flex min-h-0 flex-1">
                    <FilePreviewProvider>
                        <FileCarousel
                            onAddMediaItems={onAddMediaItems}
                            uploadLimitError={uploadLimitError}
                            clearUploadLimitError={clearUploadLimitError}
                            orderedMediaItemsOverride={orderedMediaItemsOverride}
                            autoSelectDisabled={isVideoFlatteningActive}
                            showFloatingNavigation={!isGridView}
                            showVideoControls={!isGridView}
                            isHovered={isHovered}
                        >
                            <div
                                ref={reviewCanvasContainerRef}
                                className="relative flex min-h-0 flex-1 flex-col"
                            >
                                <div
                                    className={
                                        isGridView ? "hidden" : "flex min-h-0 flex-1 flex-col"
                                    }
                                    onClickCapture={showEditPromptAtEvent}
                                >
                                    <ReviewFilePreview
                                        showLabels={showLabels}
                                        fileId={currentMedia?.file.id}
                                        annotationSource={annotationSource}
                                        forceViewOnly={!isReviewFrameEditing}
                                        predictionConfidenceThreshold={reviewConfidenceThreshold}
                                        onAnnotationClick={handleReviewAnnotationClick}
                                    />
                                </div>
                                <ReviewGridView
                                    onSelectMedia={handleSelectMediaFromGrid}
                                    visible={isGridView}
                                    annotationSource={gridAnnotationSource}
                                    orderedMediaItemsOverride={orderedMediaItemsOverride}
                                    flattenedVideoSourceId={flattenedVideoSourceId}
                                />
                                <ReviewTabs
                                    activeTab={activeTab}
                                    onTabChange={setActiveTab}
                                    isGridView={isGridView}
                                    onToggleGridView={toggleGridView}
                                    visible={true}
                                    isFrameEditing={isEditingModel}
                                    onToggleFrameEditing={toggleFrameEditing}
                                    showEditToggle={true}
                                    editDisabled={!isEditingModel && !canStartEditingCurrentFrame}
                                    editLabel={editLabel}
                                />
                                {annotationEditPrompt && (
                                    <div
                                        className="floatingContainer solid absolute z-50 p-1"
                                        style={{
                                            left: annotationEditPrompt.left,
                                            top: annotationEditPrompt.top
                                        }}
                                    >
                                        <Button
                                            type="dark-secondary"
                                            size="xsmall"
                                            onClick={handleEditPromptClick}
                                        >
                                            <i className="fas fa-pen" />
                                            <span className="ml-1">{editPromptLabel}</span>
                                        </Button>
                                    </div>
                                )}
                            </div>
                        </FileCarousel>
                    </FilePreviewProvider>
                </div>
            </div>
        </AnimatePresence>
    );
});

ReviewVisualAreaContent.displayName = "ReviewVisualAreaContent";
